import React, { FC, Fragment, useMemo, useState } from 'react';
import type {
  Blocker,
  DomainCount,
  WebWorkBlockedKindStats,
  WebWorkBlockedWeeklyBucket,
  WebWorkBlockerStats,
  WebWorkMixResponse,
} from './types';
import { BLOCKERS, KIND_COLOURS, RANK_PALETTE } from './types';
import { KindStackChart, StackSeries } from './KindStackChart';
import { SiteList } from './KindTable';
import { DASH, fmtInt, fmtShare } from './format';

/**
 * "Agent tried & got blocked" — the subset of human web work where the browser
 * agent made at least one attempt and did not finish by itself: web_action
 * tasks created after a browser attempt on the same sub-thread, plus every
 * intervention (an intervention is by definition an interrupted attempt).
 * Interventions are also shown on their own card, and the CAPTCHA mini-panel
 * tells the CAPTCHA story in one place.
 */

export interface BlockedSectionProps {
  blocked: WebWorkMixResponse['blocked'];
}

export function blockerSeries(
  weekly: WebWorkBlockedWeeklyBucket[],
  blockers: WebWorkBlockerStats[],
): StackSeries[] {
  const ranked = blockers.length
    ? blockers.map((b) => b.blocker)
    : ([] as Blocker[]);
  const present = new Set(ranked);
  BLOCKERS.forEach((b) => {
    if (!present.has(b) && weekly.some((w) => (w.byBlocker[b] ?? 0) > 0))
      ranked.push(b);
  });
  const labelOf = (b: Blocker) =>
    blockers.find((x) => x.blocker === b)?.label ?? b;
  return ranked.map((b, i) => ({
    key: b,
    label: labelOf(b),
    colour: RANK_PALETTE[i % RANK_PALETTE.length],
    values: weekly.map((w) => w.byBlocker[b] ?? 0),
  }));
}

const DomainMini: FC<{ domains: DomainCount[]; total: number }> = ({
  domains,
  total,
}) => {
  const max = Math.max(1, ...domains.map((d) => d.count));
  return (
    <table className='wwm-mini-table'>
      <tbody>
        {domains.length === 0 && (
          <tr>
            <td className='wwm-muted'>No sites recorded</td>
          </tr>
        )}
        {domains.map((d) => (
          <tr key={`${d.domain}-${d.inferred ? 'inf' : 'own'}`}>
            <td>
              <span
                className={`wwm-site ${d.domain === 'unknown' ? 'unknown' : ''}`}
              >
                {d.domain}
              </span>
              {d.inferred && (
                <span className='wwm-badge inferred'>inferred</span>
              )}
            </td>
            <td className='num'>{fmtInt(d.count)}</td>
            <td className='num wwm-muted'>{fmtShare(d.count, total)}</td>
            <td className='wwm-bar-cell'>
              <div className='wwm-bar'>
                <i style={{ width: `${(d.count / max) * 100}%` }} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const BlockerTable: FC<{
  blockers: WebWorkBlockerStats[];
  weekly: WebWorkBlockedWeeklyBucket[];
  total: number;
}> = ({ blockers, weekly, total }) => {
  const [open, setOpen] = useState<Blocker | null>(null);
  if (blockers.length === 0)
    return <p className='wwm-empty'>No blockers recorded in this window.</p>;
  const current = weekly.find((w) => w.isCurrent) ?? null;
  const lastFull = [...weekly].reverse().find((w) => !w.isCurrent) ?? null;
  return (
    <table className='wwm-table' data-testid='blocker-table'>
      <thead>
        <tr>
          <th>Blocker</th>
          <th className='num'>Count</th>
          <th className='num'>Share</th>
          <th className='num'>This wk so far</th>
          <th className='num'>Last full wk</th>
        </tr>
      </thead>
      <tbody>
        {blockers.map((b, i) => {
          const isOpen = open === b.blocker;
          return (
            <Fragment key={b.blocker}>
              <tr
                className={`wwm-row ${isOpen ? 'open' : ''}`}
                onClick={() => setOpen(isOpen ? null : b.blocker)}
                aria-expanded={isOpen}
                data-testid={`blocker-row-${b.blocker}`}
              >
                <td>
                  <span
                    className='wwm-swatch'
                    style={{
                      background: RANK_PALETTE[i % RANK_PALETTE.length],
                    }}
                  />
                  {b.label}
                </td>
                <td className='num'>{fmtInt(b.total)}</td>
                <td className='num'>{fmtShare(b.total, total)}</td>
                <td className='num'>
                  {fmtInt(current?.byBlocker[b.blocker] ?? 0)}
                </td>
                <td className='num'>
                  {fmtInt(lastFull?.byBlocker[b.blocker] ?? 0)}
                </td>
              </tr>
              {isOpen && (
                <tr className='wwm-detail'>
                  <td colSpan={5}>
                    <div className='wwm-detail-cols'>
                      <div>
                        <h5>Sites</h5>
                        <SiteList domains={b.domains} total={b.total} />
                      </div>
                      <div>
                        <h5>Literal short reasons</h5>
                        <ul className='wwm-short-reasons'>
                          {b.sampleShortReasons.length === 0 ? (
                            <li className='wwm-muted'>{DASH}</li>
                          ) : (
                            b.sampleShortReasons.map((s) => (
                              <li key={s}>“{s}”</li>
                            ))
                          )}
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

const BlockedKindTable: FC<{
  kinds: WebWorkBlockedKindStats[];
  total: number;
}> = ({ kinds, total }) => {
  const [open, setOpen] = useState<string | null>(null);
  if (kinds.length === 0)
    return <p className='wwm-empty'>No blocked task kinds in this window.</p>;
  return (
    <table className='wwm-table' data-testid='blocked-kind-table'>
      <thead>
        <tr>
          <th>Kind</th>
          <th className='num'>Count</th>
          <th className='num'>Share</th>
          <th>Top blockers</th>
        </tr>
      </thead>
      <tbody>
        {kinds.map((k) => {
          const isOpen = open === k.kind;
          return (
            <Fragment key={k.kind}>
              <tr
                className={`wwm-row ${isOpen ? 'open' : ''}`}
                onClick={() => setOpen(isOpen ? null : k.kind)}
                aria-expanded={isOpen}
                data-testid={`blocked-kind-row-${k.kind}`}
              >
                <td>
                  <span
                    className='wwm-swatch'
                    style={{ background: KIND_COLOURS[k.kind] }}
                  />
                  {k.label}
                </td>
                <td className='num'>{fmtInt(k.total)}</td>
                <td className='num'>{fmtShare(k.total, total)}</td>
                <td className='wwm-muted'>
                  {k.blockers.length
                    ? k.blockers
                        .slice(0, 3)
                        .map((b) => `${b.label} (${fmtInt(b.count)})`)
                        .join(' · ')
                    : DASH}
                </td>
              </tr>
              {isOpen && (
                <tr className='wwm-detail'>
                  <td colSpan={4}>
                    <div className='wwm-detail-cols'>
                      <div>
                        <h5>Sites</h5>
                        <SiteList domains={k.domains} total={k.total} />
                      </div>
                      <div>
                        <h5>Blockers</h5>
                        <ul className='wwm-reasons'>
                          {k.blockers.map((b) => (
                            <li key={b.blocker}>
                              <span>{b.label}</span>
                              <span className='wwm-count'>
                                {fmtInt(b.count)}
                              </span>
                              <span className='wwm-share'>
                                {fmtShare(b.count, k.total)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
};

export const BlockedSection: FC<BlockedSectionProps> = ({ blocked }) => {
  const series = useMemo(
    () => blockerSeries(blocked.weekly, blocked.blockers),
    [blocked.weekly, blocked.blockers],
  );
  const { interventionDetail, captcha } = blocked;
  const captchaKinds = Object.entries(captcha.byChallengeKind).sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );

  return (
    <section className='wwm-section' data-testid='blocked-section'>
      <h3 id='wwm-blocked'>Agent tried &amp; got blocked</h3>
      <p className='wwm-sub'>
        Only human web work where the browser agent made at least one attempt
        and did not finish by itself: web_action tasks created after a browser
        attempt on the same sub-thread, plus every intervention. Work handed to
        a human with no browser attempt is excluded.
      </p>

      <div className='wwm-tiles'>
        <div className='wwm-tile'>
          <div className='k'>Blocked → human · window</div>
          <div className='v'>{fmtInt(blocked.total)}</div>
          <div className='d'>web_action after attempt + interventions</div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>web_actions after a browser attempt</div>
          <div className='v'>{fmtInt(blocked.webActionsAfterAttempt)}</div>
          <div className='d'>
            {fmtShare(blocked.webActionsAfterAttempt, blocked.total)} of blocked
          </div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>Interventions</div>
          <div className='v'>{fmtInt(blocked.interventions)}</div>
          <div className='d'>
            {fmtShare(blocked.interventions, blocked.total)} of blocked
          </div>
        </div>
        <div className='wwm-tile'>
          <div className='k'>CAPTCHA-blocked</div>
          <div className='v'>{fmtInt(captcha.total)}</div>
          <div className='d'>
            {fmtShare(captcha.total, blocked.total)} of blocked
          </div>
        </div>
      </div>

      <div className='scaling-card wwm-card'>
        <h4>Blocked work, week over week — stacked by blocker</h4>
        {series.length === 0 ? (
          <p className='wwm-empty'>No blocked work in this window.</p>
        ) : (
          <KindStackChart
            buckets={blocked.weekly}
            series={series}
            testId='blocker-chart'
            ariaLabel='Blocked human web work per ISO week, stacked by blocker'
          />
        )}
        <p className='wwm-note'>
          Stacked by blocker — what stopped the machine. Hatched = current week
          so far.
        </p>
      </div>

      <h4>Blockers — most to least frequent</h4>
      <div className='scaling-card wwm-card wwm-card--table'>
        <BlockerTable
          blockers={blocked.blockers}
          weekly={blocked.weekly}
          total={blocked.total}
        />
        <p className='wwm-note'>
          Click a blocker for the sites it hits and the literal short reasons.
        </p>
      </div>

      <h4>Task kinds within the blocked subset</h4>
      <div className='scaling-card wwm-card wwm-card--table'>
        <BlockedKindTable kinds={blocked.kinds} total={blocked.total} />
      </div>

      <div className='wwm-cols'>
        <div
          className='scaling-card wwm-card wwm-mini'
          data-testid='interventions-card'
        >
          <h4>
            Interventions{' '}
            <span className='wwm-muted'>
              {fmtInt(interventionDetail.total)} in window
            </span>
          </h4>
          <p className='wwm-note'>
            Browser interventions on their own — every intervention is an
            interrupted browser attempt, so all of them count as blocked.
          </p>
          <h5>By blocker</h5>
          <ul className='wwm-reasons'>
            {interventionDetail.byBlocker.length === 0 && (
              <li className='wwm-muted'>{DASH}</li>
            )}
            {interventionDetail.byBlocker.map((b) => (
              <li key={b.blocker}>
                <span>{b.label}</span>
                <span className='wwm-count'>{fmtInt(b.count)}</span>
                <span className='wwm-share'>
                  {fmtShare(b.count, interventionDetail.total)}
                </span>
              </li>
            ))}
          </ul>
          <h5>By task kind</h5>
          <ul className='wwm-reasons'>
            {interventionDetail.byKind.length === 0 && (
              <li className='wwm-muted'>{DASH}</li>
            )}
            {interventionDetail.byKind.map((k) => (
              <li key={k.kind}>
                <span>
                  <span
                    className='wwm-swatch'
                    style={{ background: KIND_COLOURS[k.kind] }}
                  />
                  {k.label}
                </span>
                <span className='wwm-count'>{fmtInt(k.count)}</span>
                <span className='wwm-share'>
                  {fmtShare(k.count, interventionDetail.total)}
                </span>
              </li>
            ))}
          </ul>
          <h5>By site</h5>
          <DomainMini
            domains={interventionDetail.domains}
            total={interventionDetail.total}
          />
        </div>

        <div
          className='scaling-card wwm-card wwm-mini'
          data-testid='captcha-panel'
        >
          <h4>
            CAPTCHA{' '}
            <span className='wwm-muted'>{fmtInt(captcha.total)} in window</span>
          </h4>
          <p className='wwm-note'>
            Blocked work whose blocker is a CAPTCHA, by challenge kind (from the
            CAPTCHA classifier where available) and by site.
          </p>
          <h5>By challenge kind</h5>
          <table className='wwm-mini-table'>
            <tbody>
              {captchaKinds.length === 0 && (
                <tr>
                  <td className='wwm-muted'>No CAPTCHA-blocked work</td>
                </tr>
              )}
              {captchaKinds.map(([kind, count]) => (
                <tr key={kind}>
                  <td>{kind}</td>
                  <td className='num'>{fmtInt(count)}</td>
                  <td className='num wwm-muted'>
                    {fmtShare(count, captcha.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <h5>By site</h5>
          <DomainMini domains={captcha.domains} total={captcha.total} />
        </div>
      </div>
    </section>
  );
};
